import React from 'react';
import { uniqueId } from 'lodash';
import colors from '@honeybook/hbui/dist/styles/colors';
import LayoutIcon2ColsImageQuestions from '@honeybook/icons/dist/icons/LayoutIcon2ColsImageQuestions';
import LayoutIcon2ColsImageText from '@honeybook/icons/dist/icons/LayoutIcon2ColsImageText';
import LayoutIcon2ColsTextQuestions from '@honeybook/icons/dist/icons/LayoutIcon2ColsTextQuestions';
import LayoutIcon2ColsTextVideo from '@honeybook/icons/dist/icons/LayoutIcon2ColsTextVideo';
import LayoutIcon2ColumnsImages from '@honeybook/icons/dist/icons/LayoutIcon2ColumnsImages';
import LayoutIcon2ColumnsText from '@honeybook/icons/dist/icons/LayoutIcon2ColumnsText';
import LayoutIcon2ColumnTextImages from '@honeybook/icons/dist/icons/LayoutIcon2ColumnTextImages';
import LayoutIconCircleImageText from '@honeybook/icons/dist/icons/LayoutIconCircleImageText';
import LayoutIconGridImages from '@honeybook/icons/dist/icons/LayoutIconGridImages';
import LayoutIconGridTextImages from '@honeybook/icons/dist/icons/LayoutIconGridTextImages';
import LayoutIconImageText from '@honeybook/icons/dist/icons/LayoutIconImageText';
import LayoutIconSplash from '@honeybook/icons/dist/icons/LayoutIconSplash';
import LayoutIconTitleText from '@honeybook/icons/dist/icons/LayoutIconTitleText';
import LayoutIconVideoText from '@honeybook/icons/dist/icons/LayoutIconVideoText';
import QuestionnaireLayout from '@honeybook/icons/dist/icons/QuestionnaireLayout';
import TextProofingLayout from '@honeybook/icons/dist/icons/TextProffingLayout';
import ImageProofingLayout from '@honeybook/icons/dist/icons/ImageProofingLayout';
import ImageSelectionLayout from '@honeybook/icons/dist/icons/ImageSelectionLayout';
import DefaultQuestionThum from '@honeybook/icons/dist/icons/DefaultQuestionThum';
import DefaultImageThum from '@honeybook/icons/dist/icons/DefaultImageThum';
import DefaultTextThum from '@honeybook/icons/dist/icons/DefaultTextThum';
import DefaultVideoThum from '@honeybook/icons/dist/icons/DefaultVideoThum';
import QuestionUploadLayout from '@honeybook/icons/dist/icons/QuestionUploadLayout';
import FormLayout from '@honeybook/icons/dist/icons/FormLayout';
import {
    CompositionCategory,
    ICompositionLayoutTypeConfig
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import {
    CompositionLayoutType,
    QuestionType,
    TCompositionLayoutType
} from '~/types/builder.types';
import { Alignment, ContentWidth } from '~/types/block-styles.types';

export const COMPOSITION_LAYOUTS: Record<
    CompositionLayoutType,
    ICompositionLayoutTypeConfig
> = {
    [CompositionLayoutType.OneQuestion]: {
        title: '1 question',
        icon: <DefaultQuestionThum useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.OneQuestion,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.ShortText,
                                text: ''
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.TextOnly]: {
        title: 'Text only',
        icon: <DefaultTextThum useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.TextOnly,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        { type: 'text_block', text_style: { type: 'title' } }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.OneImage]: {
        title: '1 image',
        icon: <DefaultImageThum useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.OneImage,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [{ type: 'image_block' }]
                }
            ]
        }
    },
    [CompositionLayoutType.OneVideo]: {
        title: '1 video',
        icon: <DefaultVideoThum useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.OneVideo,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [{ type: 'video_block' }]
                }
            ]
        }
    },
    [CompositionLayoutType.Questionnaire]: {
        category: CompositionCategory.Popular,
        title: 'Questionnaire',
        icon: <QuestionnaireLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.Questionnaire,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { content_width: ContentWidth.LARGE },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.ShortText,
                                text: ''
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.Dropdown,
                                text: '',
                                options: [
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' }
                                ]
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.LongText,
                                text: ''
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImageAndQuestions]: {
        category: CompositionCategory.Popular,
        title: 'Questions & image',
        icon: <LayoutIcon2ColsImageQuestions useDefaultFill />,
        smallIcon: <LayoutIcon2ColsImageQuestions useDefaultFill />,
        type: CompositionLayoutType.ImageAndQuestions,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 40 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [{ type: 'image_block' }]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.ShortText,
                                text: ''
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.Dropdown,
                                options: [],
                                text: ''
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.MultiChoice,
                                options: [
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' }
                                ],
                                text: ''
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.Form]: {
        category: CompositionCategory.Popular,
        title: 'Form',
        icon: <FormLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.Form,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: {
                content_width: ContentWidth.LARGE,
                grid_gap: 60
            },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.ShortText,
                                text: ''
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.Dropdown,
                                text: '',
                                options: [
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' }
                                ]
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.LongText,
                                text: ''
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.MultiChoice,
                                text: '',
                                description:
                                    'Choose one or more of the options',
                                options: [
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' }
                                ]
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.SingleChoice,
                                text: '',
                                description: 'Choose one of the options',
                                options: [
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' },
                                    { option_id: uniqueId(), answer_text: '' }
                                ]
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.TextApproval]: {
        category: CompositionCategory.Popular,
        title: 'Text approval',
        icon: <TextProofingLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.TextApproval,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'text_block',
                            text_content:
                                'Add the text your client needs to approve',
                            text_style: { type: 'regular' }
                        },
                        {
                            type: 'question_block',
                            question: {
                                text: 'Mark the checkbox to approve',
                                question_type: QuestionType.MultiChoice,
                                options: [
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'I approve the text'
                                    }
                                ]
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.LongText,
                                text: 'Have any comments?'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImageApproval]: {
        category: CompositionCategory.Popular,
        title: 'Image approval',
        icon: <ImageProofingLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.ImageApproval,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { content_width: ContentWidth.LARGE },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'image_block'
                        },
                        {
                            type: 'question_block',
                            question: {
                                text: 'Mark the checkbox to approve',
                                question_type: QuestionType.MultiChoice,
                                options: [
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'I approve the image'
                                    }
                                ]
                            }
                        },
                        {
                            type: 'question_block',
                            question: {
                                text: 'Have any comments?',
                                question_type: QuestionType.LongText
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImageSelection]: {
        title: 'Image selection',
        icon: <ImageSelectionLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.ImageSelection,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: {
                content_width: ContentWidth.LARGE,
                grid_gap: 40
            },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'image_block'
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_content: 'Image 1',
                            text_style: {
                                type: 'regular',
                                color: colors.neutral600
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'image_block'
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_content: 'Image 3',
                            text_style: {
                                type: 'regular',
                                color: colors.neutral600
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'question_block',
                            question: {
                                text: 'Choose one image',
                                question_type: QuestionType.SingleChoice,
                                options: [
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'Image 1'
                                    },
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'Image 2'
                                    },
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'Image 3'
                                    },
                                    {
                                        option_id: uniqueId(),
                                        answer_text: 'Image 4'
                                    }
                                ]
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'image_block'
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_content: 'Image 2',
                            text_style: {
                                type: 'regular',
                                color: colors.neutral600
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'image_block'
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_content: 'Image 4',
                            text_style: {
                                type: 'regular',
                                color: colors.neutral600
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        }
                    ]
                }
            ]
        }
    },

    [CompositionLayoutType.IconAndText]: {
        category: CompositionCategory.Popular,
        title: 'Icon & text',
        icon: <LayoutIconCircleImageText useDefaultFill />,
        smallIcon: <LayoutIconCircleImageText useDefaultFill />,
        type: CompositionLayoutType.IconAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: 'Circle' },
                            image_styles: {
                                border_radius: 100
                            }
                        },
                        {
                            type: 'spacer_block'
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'title', alignment: 'center' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.TitleAndText]: {
        category: CompositionCategory.Basic,
        title: 'Title & text',
        icon: <LayoutIconTitleText useDefaultFill />,
        smallIcon: <LayoutIconTitleText useDefaultFill />,
        type: CompositionLayoutType.TitleAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'two_rows_description' }
                        }
                    ]
                }
            ]
        }
    },

    [CompositionLayoutType.TextAndVideo]: {
        category: CompositionCategory.Basic,
        title: 'Text & video',
        icon: <LayoutIcon2ColsTextVideo useDefaultFill />,
        smallIcon: <LayoutIcon2ColsTextVideo useDefaultFill />,
        type: CompositionLayoutType.TextAndVideo,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [{ type: 'video_block' }]
                }
            ]
        }
    },
    [CompositionLayoutType.TwoColumnText]: {
        category: CompositionCategory.Basic,
        title: '2 column text',
        icon: <LayoutIcon2ColumnsText useDefaultFill />,
        smallIcon: <LayoutIcon2ColumnsText useDefaultFill />,
        type: CompositionLayoutType.TwoColumnText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 40 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.TwoImages]: {
        category: CompositionCategory.Basic,
        title: '2 images',
        icon: <LayoutIcon2ColumnsImages useDefaultFill />,
        smallIcon: <LayoutIcon2ColumnsImages useDefaultFill />,
        type: CompositionLayoutType.TwoImages,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [{ type: 'image_block' }]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [{ type: 'image_block' }]
                }
            ]
        }
    },
    [CompositionLayoutType.ImagesAndTextGrid]: {
        category: CompositionCategory.Popular,
        title: 'Images & text grid',
        icon: <LayoutIconGridTextImages useDefaultFill />,
        smallIcon: <LayoutIconGridTextImages useDefaultFill />,
        type: CompositionLayoutType.ImagesAndTextGrid,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 60 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: 'Circle' },
                            image_styles: {
                                border_radius: 100
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'short_title',
                                alignment: 'center'
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: 'Circle' },
                            image_styles: {
                                border_radius: 100
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'short_title',
                                alignment: 'center'
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: 'Circle' },
                            image_styles: {
                                border_radius: 100
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'short_title',
                                alignment: 'center'
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.Splash]: {
        category: CompositionCategory.Popular,
        title: 'Splash',
        icon: <LayoutIconSplash useDefaultFill />,
        smallIcon: <LayoutIconSplash useDefaultFill />,
        type: CompositionLayoutType.Splash,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            background_image: { url: null },
            container_styles: {
                background_color: colors.black,
                background_color_opacity: 10
            },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 100 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'title',
                                alignment: 'center',
                                color: colors.white
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'two_rows_description',
                                alignment: 'center',
                                color: colors.white
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 100 }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.VideoAndText]: {
        category: CompositionCategory.Popular,
        title: 'Video & text',
        icon: <LayoutIconVideoText useDefaultFill />,
        smallIcon: <LayoutIconVideoText useDefaultFill />,
        type: CompositionLayoutType.VideoAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        { type: 'video_block' },
                        {
                            type: 'spacer_block'
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'title', alignment: 'center' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImageAndText]: {
        category: CompositionCategory.Popular,
        title: 'Image & text',
        icon: <LayoutIconImageText useDefaultFill />,
        smallIcon: <LayoutIconImageText useDefaultFill />,
        type: CompositionLayoutType.ImageAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        { type: 'image_block' },
                        {
                            type: 'spacer_block'
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'title', alignment: 'center' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.TextAndQuestions]: {
        category: CompositionCategory.Popular,
        title: 'Text & questions',
        icon: <LayoutIcon2ColsTextQuestions useDefaultFill />,
        smallIcon: <LayoutIcon2ColsTextQuestions useDefaultFill />,
        type: CompositionLayoutType.TextAndQuestions,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 40 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.ShortText,
                                text: ''
                            }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.Dropdown,
                                options: [],
                                text: ''
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImageGrid]: {
        category: CompositionCategory.Popular,
        title: 'Image grid',
        icon: <LayoutIconGridImages useDefaultFill />,
        smallIcon: <LayoutIconGridImages useDefaultFill />,
        type: CompositionLayoutType.ImageGrid,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 0 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        },
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        },
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 33.33,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        },
                        {
                            type: 'image_block',
                            image: { crop_ratio: '1:1' },
                            image_styles: { alignment: Alignment.STRETCH }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.LeftImageAndText]: {
        category: CompositionCategory.Basic,
        title: 'Left image & text',
        icon: <LayoutIcon2ColsImageText useDefaultFill />,
        smallIcon: <LayoutIcon2ColsImageText useDefaultFill />,
        type: CompositionLayoutType.LeftImageAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'image_block',
                            image: { crop_ratio: 'Circle' },
                            image_styles: {
                                border_radius: 100
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        {
                            type: 'text_block',
                            text_style: { type: 'title' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.ImagesAndText]: {
        category: CompositionCategory.Basic,
        title: 'Images & text',
        icon: <LayoutIcon2ColumnTextImages useDefaultFill />,
        smallIcon: <LayoutIcon2ColumnTextImages useDefaultFill />,
        type: CompositionLayoutType.ImagesAndText,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            container_styles: { grid_gap: 60 },
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        { type: 'image_block' },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'title', alignment: 'center' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                },
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 50,
                    children: [
                        { type: 'image_block' },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 20 }
                        },
                        {
                            type: 'text_block',
                            text_style: { type: 'title', alignment: 'center' }
                        },
                        {
                            type: 'spacer_block',
                            spacer_styles: { height: 10 }
                        },
                        {
                            type: 'text_block',
                            text_style: {
                                type: 'description',
                                alignment: 'center'
                            }
                        }
                    ]
                }
            ]
        }
    },
    [CompositionLayoutType.UploadQuestion]: {
        title: 'File upload',
        icon: <QuestionUploadLayout useDefaultFill />,
        smallIcon: null,
        type: CompositionLayoutType.UploadQuestion,
        layoutStruct: {
            type: 'container_block',
            layout_type: 'horizontal',
            children: [
                {
                    type: 'container_block',
                    layout_type: 'vertical',
                    size: 100,
                    children: [
                        {
                            type: 'question_block',
                            question: {
                                question_type: QuestionType.Upload,
                                text: ''
                            }
                        }
                    ]
                }
            ]
        }
    }
};

// When releasing a new layout behind a feature flag, add its key to this array
export const DEFAULT_EXCLUDED_LAYOUT_TYPES: TCompositionLayoutType[] = [];
